import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import useConvenienceFees from '@/hooks/useApi/useConvenienceFees';
import { usePaylink } from './Paylink.provider';

type UseConvenienceFeesReturnValues = ReturnType<typeof useConvenienceFees>;

interface ConvenienceFeesContextType {
  convenienceFees: UseConvenienceFeesReturnValues['convenienceFees'];
  showFees: UseConvenienceFeesReturnValues['showFees'];
  getProcessingFee: UseConvenienceFeesReturnValues['getProcessingFee'];
  getLowestAndHighestFees: UseConvenienceFeesReturnValues['getLowestAndHighestFees'];
  isLoading: boolean;
  error: string | null;
}

const ConvenienceFeesContext = createContext<ConvenienceFeesContextType | undefined>(undefined);

interface ConvenienceFeesProviderProps {
  children: ReactNode;
}

export const ConvenienceFeesProvider: React.FC<ConvenienceFeesProviderProps> = ({ children }) => {
  const { Token } = usePaylink();
  const { fetchConvenienceFees, getProcessingFee, getLowestAndHighestFees, convenienceFees, showFees } =
    useConvenienceFees();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (Token) {
      const fetchData = async () => {
        try {
          setIsLoading(true);
          setError(null);
          await fetchConvenienceFees();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch convenience fees');
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [Token]);

  const value: ConvenienceFeesContextType = {
    convenienceFees,
    showFees,
    getProcessingFee,
    getLowestAndHighestFees,
    isLoading,
    error,
  };

  return <ConvenienceFeesContext.Provider value={value}>{children}</ConvenienceFeesContext.Provider>;
};

export const useConvenienceFeesProvider = (): ConvenienceFeesContextType => {
  const context = useContext(ConvenienceFeesContext);
  if (context === undefined) {
    throw new Error('useConvenienceFeesProvider must be used within a ConvenienceFeesProvider');
  }
  return context;
};
